<template>
    <grid
        ref="grid"
        :isSearch="false"
        :columnDefs="columnDefs"
        :rowData="rowData"
        v-if="details"
    >
        <div class="d-flex flex-column mr-3">
            <span>Training Complete: </span>
            <form-square-switch
                class="mr-2 account-details-switch"
                :labels="{
                    checked: 'ON',
                    unchecked: 'OFF',
                }"
                :isToolbar="true"
                :sync="true"
                v-model="
                    details.client.clientsettings.insurance_training_completed
                "
                @input="setTrainingComplete"
            />
        </div>
        <div class="d-flex flex-column mr-3">
            <span>Allow Damage Protection: </span>
            <form-square-switch
                class="mr-2 damage-protection-switch"
                :labels="{
                    checked: 'ON',
                    unchecked: 'OFF',
                }"
                :isToolbar="true"
                :sync="true"
                v-model="details.client.clientsettings.allow_insurances"
                @input="setAllowInsurance"
            />
        </div>
    </grid>
</template>

<script>
import FormSquareSwitch from '../../../generic/form-square-switch/form-square-switch.vue';

import api from '../../../../api';
import { ADMIN_SUBSCRIBER_DAMAGE_PROTECTION } from '../../../../endpoints';
import grid from '../../../generic/grid/grid.vue';
import { mapGetters } from 'vuex';
import { toAmountDisplay } from '../../../../utils/numbers';
import { formatResponseError } from '../../../../utils/api';

export default {
    name: 'SubscriberPayments',
    components: {
        grid,
        FormSquareSwitch,
    },
    data() {
        return {
            rowData: [],
        };
    },
    inject: ['userId'],
    mounted() {
        this.fetchInsurances();
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        ...mapGetters('adminSubscriberAccount', ['details']),

        columnDefs() {
            return [
                {
                    field: 'id',
                    headerName: 'Product ID',
                    maxWidth: 120,
                    minWidth: 120,
                },
                {
                    field: 'product_coverage',
                    headerName: 'Product Code',
                    cellRenderer: ({ data }) =>
                        data && `SDW${data.product_coverage}`,
                    minWidth: 150,
                },
                {
                    field: 'product_name',
                    headerName: 'Name',
                    minWidth: 150,
                },
                {
                    field: 'srp',
                    headerName: 'SRP',
                    maxWidth: 120,
                    minWidth: 120,
                },
                {
                    field: 'discount',
                    headerName: 'Discount',
                    cellEditor: 'selectCellEditor',
                    minWidth: 140,
                    maxWidth: 140,
                    editable: true,
                    cellClass: 'text-primary lx-editable-cell',
                    cellRenderer: ({ data }) =>
                        data &&
                        toAmountDisplay({
                            amount: data.discount,
                            percentage: true,
                            decimalPlaces: 0,
                        }),
                    cellEditorParams: ({ data }) => ({
                        options: () =>
                            this.details &&
                            this.details.discount_choices.map((item) => ({
                                value: item.value,
                                text: item.name,
                            })),
                    }),
                    valueGetter: ({ data }) => data.discount,
                    valueSetter: async ({ node, data, newValue, oldValue }) => {
                        (async () => {
                            try {
                                await api.post(
                                    `${ADMIN_SUBSCRIBER_DAMAGE_PROTECTION(
                                        this.userId
                                    )}set-discount/`,
                                    {
                                        insurance_pk: data.id,
                                        discount: newValue,
                                    }
                                );
                                this.successToast({
                                    text: 'Damage Protection discount successfully updated.',
                                });
                                this.fetchInsurances();
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                if (generic) {
                                    this.errorToast({ text: generic });
                                } else {
                                    this.errorToast({
                                        text: 'Could not set discount',
                                    });
                                }

                                data.discount = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.discount = newValue;
                        return true;
                    },
                },
                {
                    field: 'wholesale_cost',
                    headerName: 'Cost $',
                    maxWidth: 120,
                    minWidth: 120,
                },
                {
                    field: 'retail_cost',
                    headerName: 'Retal $',
                    maxWidth: 120,
                    minWidth: 120,
                },
            ];
        },
    },
    methods: {
        async fetchInsurances() {
            try {
                this.$parent.$emit('busy', true);
                const { data } = await api.get(
                    `${ADMIN_SUBSCRIBER_DAMAGE_PROTECTION(
                        this.userId
                    )}insurances/`
                );
                this.rowData = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                if (generic) {
                    this.errorToast({ text: generic });
                } else {
                    this.errorToast({ text: 'Could not fetch insurances' });
                }
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async setTrainingComplete(value) {
            try {
                this.$parent.$emit('busy', true);
                await api.post(
                    `${ADMIN_SUBSCRIBER_DAMAGE_PROTECTION(
                        this.userId
                    )}set-training/`,
                    {
                        is_complete: value,
                    }
                );
                this.successToast({
                    text: 'Damage Protection Training successfully updated.',
                });
            } catch (e) {
                const { generic } = formatResponseError(e);
                if (generic) {
                    this.errorToast({ text: generic });
                } else {
                    this.errorToast({ text: 'Could not set value' });
                }
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async setAllowInsurance(value) {
            try {
                this.$parent.$emit('busy', true);
                await api.post(
                    `${ADMIN_SUBSCRIBER_DAMAGE_PROTECTION(
                        this.userId
                    )}set-allow-insurances/`,
                    {
                        is_allow: value,
                    }
                );
                this.successToast({
                    text: 'Allow Damage Protection successfully updated.',
                });
            } catch (e) {
                const { generic } = formatResponseError(e);
                if (generic) {
                    this.errorToast({ text: generic });
                } else {
                    this.errorToast({ text: 'Could not set value' });
                }
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.damage-protection-switch {
    width: 120px;
}
</style>